"use client";

import { type ReactNode, createContext, useRef, useContext } from "react";
import { useStore } from "zustand";

import { type AuthStore, createAuthStore, initAuthStore } from "@/stores/auth-store";
import { AuthResult } from "@/utils/auth";

export type AuthStoreApi = ReturnType<typeof createAuthStore>;

export const AuthStoreContext = createContext<AuthStoreApi | undefined>(undefined);

export interface AuthStoreProviderProps {
  auth: AuthResult;
  children: ReactNode;
}

export const AuthStoreProvider = ({ auth, children }: AuthStoreProviderProps) => {
  const storeRef = useRef<AuthStoreApi | null>(null);
  if (storeRef.current === null) {
    storeRef.current = createAuthStore(initAuthStore(auth));
  }

  return <AuthStoreContext.Provider value={storeRef.current}>{children}</AuthStoreContext.Provider>;
};

export const useAuthStore = <T,>(selector: (store: AuthStore) => T): T => {
  const context = useContext(AuthStoreContext);

  if (!context) {
    throw new Error(`useAuthStore must be used within AuthStoreProvider`);
  }

  return useStore(context, selector);
};
